import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { poolAPI, scoringAPI, chatAPI, entryAPI, feeTrackingAPI, adminAPI, matchAPI, dataAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowUpDown, Search, Users, DollarSign, Copy, MessageSquare, Trophy, Calculator, ChevronUp, ChevronDown, ChevronRight, Megaphone, Check, X, RefreshCw, Download, Tv, BookOpen, Eye, EyeOff } from 'lucide-react';

// ========== WEEKLY BREAKDOWN ACCORDION ==========
function WeeklyAccordion({ entryId, picks }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await entryAPI.weeklyBreakdown(entryId);
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [entryId]);

  if (loading) return <div className="px-4 py-6"><div className="h-32 bg-muted animate-pulse rounded-lg" /></div>;
  if (!data || !data.weekly?.length) return <div className="px-4 py-4 text-sm text-muted-foreground">No match data available yet.</div>;

  const teamNames = picks?.map(p => p.team_name) || [];

  return (
    <div className="px-2 sm:px-5 py-3 bg-muted/30">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm" style={{ minWidth: '600px' }}>
          <thead>
            <tr className="border-b">
              <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground text-xs">Week</th>
              {teamNames.map((name, i) => {
                const tier = picks[i]?.tier;
                return <th key={name} className="px-3 py-2.5 text-center font-semibold text-xs">{name}<br/><span className="text-[10px] text-muted-foreground font-normal">Tier {tier}</span></th>;
              })}
              <th className="px-3 py-2.5 text-right font-semibold text-xs text-muted-foreground">Week</th>
              <th className="px-3 py-2.5 text-right font-semibold text-xs text-[#5b3cc4]">Cumul.</th>
            </tr>
          </thead>
          <tbody>
            {data.weekly.map((week, wi) => (
              <tr key={wi} className="border-b border-border/30 hover:bg-accent/30">
                <td className="px-3 py-2 font-mono text-sm text-muted-foreground whitespace-nowrap">{week.round.replace('Regular Season - ', 'GW')}</td>
                {teamNames.map(name => {
                  const t = week.teams[name];
                  if (!t || t.result === '-') return <td key={name} className="px-3 py-2 text-center text-muted-foreground">-</td>;
                  const resultColor = t.result === 'W' ? 'text-green-600 font-bold' : t.result === 'D' ? 'text-yellow-600 font-medium' : 'text-red-500';
                  return (
                    <td key={name} className="px-3 py-2 text-center text-sm">
                      <span className={`${resultColor}`}>{t.result}</span>
                      <span className="text-muted-foreground ml-1">+{t.points}</span>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-mono text-sm font-semibold">{week.week_total}</td>
                <td className="px-3 py-2 text-right font-mono text-sm font-bold text-[#5b3cc4]">{week.running_total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========== LEADERBOARD TAB ==========
function LeaderboardTab({ poolId, pool }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('rank');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await scoringAPI.leaderboard(poolId, { sort_by: sortBy, sort_dir: sortDir, search, page, page_size: 50 });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [poolId, sortBy, sortDir, search, page]);

  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'total_points' ? 'desc' : 'asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };

  const prizes = data?.prizes;

  return (
    <div className="space-y-4">
      {prizes && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          {[
            { label: 'Entries', val: `${prizes.total_entries}`, color: '' },
            { label: '1st Place', val: `${prizes.first.percentage}%`, color: 'text-[#5b3cc4]' },
            { label: '2nd Place', val: `${prizes.second.percentage}%`, color: '' },
            { label: '3rd Place', val: `${prizes.third.percentage}%`, color: '' },
            { label: 'Middle', val: `${prizes.middle.percentage}%`, color: '' },
            { label: 'Goals Pred.', val: `${prizes.goals.percentage}%`, color: '' },
          ].map(p => (
            <div key={p.label} className="rounded-xl border bg-card p-2 sm:p-3 shadow-sm">
              <p className="text-[9px] sm:text-[10px] text-muted-foreground truncate">{p.label}</p>
              <p className={`font-heading text-sm sm:text-base font-bold ${p.color}`}>{p.val}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="leaderboard-search-input" placeholder="Search by name or alias..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
          <Badge variant="secondary">{data?.total || 0} entries</Badge>
          <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1"><ChevronDown className="h-3 w-3 text-[#5b3cc4]" />Click row for weekly breakdown</span>
        </div>
      </div>

      <div className="rounded-2xl border bg-card overflow-hidden" data-testid="leaderboard-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '900px' }}>
            <thead className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b">
              <tr>
                {[
                  { key: 'rank', label: '#', w: 'w-10' },
                  { key: 'team_alias', label: 'Team Name', w: 'min-w-[120px]' },
                  { key: 'total_points', label: 'Total', w: 'w-16' },
                  { key: 'name', label: 'Name', w: 'min-w-[140px]' },
                  { key: 'tier_1', label: 'Tier 1', w: 'w-20' },
                  { key: 'tier_2', label: 'Tier 2', w: 'w-20' },
                  { key: 'tier_3', label: 'Tier 3', w: 'w-20' },
                  { key: 'tier_4', label: 'Tier 4', w: 'w-20' },
                  { key: 'tier_5', label: 'Tier 5', w: 'w-20' },
                  { key: 'goals_prediction', label: 'Goals', w: 'w-14' },
                ].map(col => (
                  <th key={col.key} className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-foreground ${col.w}`}
                    onClick={() => handleSort(col.key)} data-testid={`leaderboard-sort-${col.key}`}>
                    <span className="flex items-center gap-0.5">{col.label}<SortIcon col={col.key} /></span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({length: 8}).map((_, i) => (
                  <tr key={`skel-${i}`}><td colSpan={10} className="px-2 py-2"><div className="h-5 bg-muted animate-pulse rounded" /></td></tr>
                ))
              ) : data?.leaderboard?.length === 0 ? (
                <tr><td colSpan={10} className="px-2 py-12 text-center text-muted-foreground">No entries with picks yet.</td></tr>
              ) : (
                data?.leaderboard?.map((entry, idx) => {
                  const isMe = entry.user_id === data.current_user_id;
                  const isExpanded = expandedRow === entry.entry_id;
                  return (
                    <React.Fragment key={`${entry.entry_id}-${idx}`}>
                      <tr
                        className={`border-b border-border/30 cursor-pointer transition-colors ${isMe ? 'bg-[#f3f0ff]/30' : 'odd:bg-muted/30'} hover:bg-accent/40`}
                        onClick={() => setExpandedRow(isExpanded ? null : entry.entry_id)}
                      >
                      {(() => {
                        const picksHidden = pool.status !== 'locked' && !isMe;
                        return (
                      <>
                        <td className="px-3 py-3 font-mono text-sm font-bold tabular-nums">{picksHidden ? '-' : entry.rank}</td>
                        <td className="px-3 py-3 text-sm">{entry.team_alias || '-'}</td>
                        <td className="px-3 py-3">
                          {picksHidden ? (
                            <span className="text-sm text-muted-foreground italic">Revealed at season start</span>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-lg font-bold tabular-nums text-[#5b3cc4]">{entry.total_points}</span>
                              <ChevronDown className={`h-4 w-4 text-[#5b3cc4] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <div>
                              <span className="font-medium text-sm">{entry.display_name || entry.user_name}</span>
                              {entry.entry_number > 1 && <span className="text-xs text-muted-foreground ml-1">#{entry.entry_number}</span>}
                              {isMe && <Badge className="ml-1.5 bg-[#f3f0ff] text-[#5b3cc4] text-[9px] h-4">You</Badge>}
                            </div>
                          </div>
                        </td>
                        {[1,2,3,4,5].map(t => {
                          const tp = entry.tier_points?.[String(t)];
                          const picksHidden = pool.status !== 'locked' && !isMe;
                          return (
                            <td key={t} className="px-3 py-3 text-center">
                              {picksHidden ? (
                                <>
                                  <div className="text-xs text-muted-foreground">???</div>
                                  <div className="font-mono text-sm font-bold tabular-nums text-muted-foreground">—</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-xs text-muted-foreground truncate max-w-[85px]">{tp?.team || '-'}</div>
                                  <div className="font-mono text-sm font-bold tabular-nums">{tp?.points ?? 0}</div>
                                </>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 font-mono text-sm tabular-nums">{picksHidden ? '—' : (entry.goals_prediction ?? '-')}</td>
                      </>
                      );
                      })()}
                      </tr>
                      {isExpanded && !(pool.status !== 'locked' && !isMe) && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <WeeklyAccordion entryId={entry.entry_id} picks={entry.picks} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 50 && (
        <div className="flex items-center justify-between" data-testid="leaderboard-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(data.total / 50)}</span>
          <Button variant="outline" size="sm" disabled={page * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

// ========== CHAT TAB ==========
// ========== ANNOUNCEMENTS TAB (Free tier) ==========
function AnnouncementsTab({ poolId, pool }) {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const isOrganizer = pool?.organizer_id === user?.id;
  const isAdmin = user?.role === 'admin';

  const loadAnnouncements = useCallback(async () => {
    try {
      const res = await chatAPI.list(poolId, { msg_type: 'announcement', page_size: 50 });
      setAnnouncements(res.data.messages.reverse());
    } catch (err) { console.error(err); }
  }, [poolId]);

  useEffect(() => { loadAnnouncements(); const iv = setInterval(loadAnnouncements, 30000); return () => clearInterval(iv); }, [loadAnnouncements]);

  const send = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setLoading(true);
    try {
      await chatAPI.post(poolId, newMsg.trim(), 'announcement');
      setNewMsg('');
      loadAnnouncements();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[500px]">
      <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-card rounded-t-xl border border-b-0">
        {announcements.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">No announcements yet.</p>
            {(isOrganizer || isAdmin) && <p className="text-xs text-muted-foreground mt-1">Post your first announcement below.</p>}
          </div>
        ) : announcements.map((msg) => (
          <div key={msg.id} className="rounded-xl bg-[#f3f0ff] border border-[#5b3cc4]/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-full bg-[#5b3cc4] flex-shrink-0 flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">{msg.user_name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <span className="text-sm font-semibold">{msg.user_name}</span>
              <Badge className="bg-[#5b3cc4]/10 text-[#5b3cc4] text-[8px] h-3.5">Organizer</Badge>
              <span className="text-[10px] text-muted-foreground ml-auto">{new Date(msg.created_at).toLocaleString()}</span>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
            {(isOrganizer || isAdmin) && (
              <button onClick={async () => { await chatAPI.delete(msg.id); loadAnnouncements(); }} className="text-[10px] text-destructive mt-2 hover:underline">delete</button>
            )}
          </div>
        ))}
      </div>
      {(isOrganizer || isAdmin) && (
        <form onSubmit={send} className="flex gap-2 p-3 bg-card border rounded-b-xl">
          <Textarea value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Post an announcement to all pool members..." className="min-h-[60px] max-h-[120px] resize-y text-sm" />
          <Button type="submit" disabled={loading || !newMsg.trim()} size="sm"><Megaphone className="h-3.5 w-3.5 mr-1" />Post</Button>
        </form>
      )}
    </div>
  );
}

// ========== CHAT TAB (Paid tier) ==========
function ChatTab({ poolId, pool }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const isOrganizer = pool?.organizer_id === user?.id;

  const loadMessages = useCallback(async () => {
    try {
      const res = await chatAPI.list(poolId, { msg_type: 'general', page_size: 100 });
      setMessages(res.data.messages.reverse());
    } catch (err) { console.error(err); }
  }, [poolId]);

  useEffect(() => { loadMessages(); const iv = setInterval(loadMessages, 15000); return () => clearInterval(iv); }, [loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setLoading(true);
    try {
      await chatAPI.post(poolId, newMsg.trim(), 'general');
      setNewMsg('');
      loadMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[500px]">
      <div className="flex-1 overflow-y-auto space-y-2 p-3 bg-card rounded-t-xl border border-b-0" data-testid="pool-chat-message-list">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : messages.map((msg) => (
          <div key={msg.id} className="flex gap-2">
            <div className="h-7 w-7 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
              <span className="text-[10px] font-bold text-primary-foreground">{msg.user_name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="max-w-[75%]">
              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                <span className="text-[11px] font-medium">{msg.user_name}</span>
                {msg.user_role === 'organizer' && <Badge variant="secondary" className="text-[8px] h-3.5">Organizer</Badge>}
                <span className="text-[9px] text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
              </div>
              <div className="rounded-xl bg-secondary px-3 py-2 text-sm inline-block text-left whitespace-pre-wrap">{msg.message}</div>
              {(msg.user_id === user?.id || isOrganizer || user?.role === 'admin') && (
                <button onClick={async () => { await chatAPI.delete(msg.id); loadMessages(); }} className="text-[9px] text-destructive ml-2 hover:underline">delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 bg-card border rounded-b-xl">
        <Textarea data-testid="pool-chat-input" value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Type a message..." className="min-h-[60px] max-h-[120px] resize-y text-sm" />
        <Button data-testid="pool-chat-send-button" type="submit" disabled={loading || !newMsg.trim()} size="sm">Send</Button>
      </form>
    </div>
  );
}

// ========== MANAGE TAB (Organizer) ==========
function ManageTab({ pool, onRefresh }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [computing, setComputing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberEntries, setMemberEntries] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await feeTrackingAPI.get(pool.id);
        setMembers(res.data.members);
      } catch (err) { console.error(err); }
    };
    load();
  }, [pool.id]);

  const toggleFee = async (userId, currentPaid) => {
    try {
      await feeTrackingAPI.update(pool.id, userId, !currentPaid);
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, organizer_fee_paid: !currentPaid } : m));
    } catch (err) { toast.error('Failed'); }
  };

  const toggleMemberExpand = async (userId) => {
    if (expandedMember === userId) { setExpandedMember(null); return; }
    setExpandedMember(userId);
    if (!memberEntries[userId]) {
      try {
        const res = await entryAPI.list(pool.id);
        const userE = (res.data.entries || []).filter(e => e.user_id === userId);
        setMemberEntries(prev => ({ ...prev, [userId]: userE }));
      } catch (e) { console.error(e); }
    }
  };

  const removeEntry = async (entryId, entryName) => {
    if (!window.confirm(`Remove entry "${entryName}"? This cannot be undone.`)) return;
    try {
      await entryAPI.remove(pool.id, entryId);
      toast.success('Entry removed');
      setMemberEntries(prev => ({ ...prev, [expandedMember]: (prev[expandedMember] || []).filter(e => e.id !== entryId) }));
      const res = await feeTrackingAPI.get(pool.id);
      setMembers(res.data.members);
    } catch (err) { toast.error('Failed to remove'); }
  };

  const computeStandings = async () => {
    setComputing(true);
    try {
      const res = await scoringAPI.compute(pool.id);
      toast.success(res.data.message);
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setComputing(false); }
  };

  const syncResults = async () => {
    setSyncing(true);
    try {
      const res = await adminAPI.syncResults();
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleCSVEntries = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const res = await entryAPI.importCSV(pool.id, file);
      toast.success(`Imported ${res.data.imported} entries. ${res.data.errors.length} errors.`);
    } catch (err) { toast.error('Import failed'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><RefreshCw className="h-4 w-4" />Sync & Compute</h3>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button variant="outline" size="sm" onClick={syncResults} disabled={syncing}>{syncing ? 'Syncing...' : 'Sync Results'}</Button>
            )}
            <Button size="sm" onClick={computeStandings} disabled={computing}>{computing ? 'Computing...' : 'Compute Standings'}</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Entry Fee Tracking</CardTitle>
            <Badge variant="secondary">{members.length} members</Badge>
          </div>
          <p className="text-xs text-muted-foreground">Track which entrants have sent their pool contribution to you. Entry fee: {pool.organizer_fee || 'Not set'}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.user_id} className="rounded-lg bg-secondary/30 hover:bg-secondary/50 overflow-hidden">
                <div className="flex items-center justify-between py-3 px-4 cursor-pointer" onClick={() => toggleMemberExpand(m.user_id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expandedMember === m.user_id ? 'rotate-180' : ''}`} />
                      <span className="text-sm font-semibold">{m.user_name}</span>
                      {m.team_alias && <span className="text-xs text-[#5b3cc4] font-medium">({m.team_alias})</span>}
                      <span className="text-xs text-muted-foreground">{m.entry_count} {m.entry_count === 1 ? 'entry' : 'entries'}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 ml-6">{m.user_email}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); toggleFee(m.user_id, m.organizer_fee_paid); }}
                    className={`flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full transition-colors flex-shrink-0 ${
                      m.organizer_fee_paid ? 'bg-[#f3f0ff] text-[#5b3cc4]' : 'bg-red-100 text-red-700'
                    }`}>
                    {m.organizer_fee_paid ? <><Check className="h-3 w-3" />Paid</> : <><X className="h-3 w-3" />Unpaid</>}
                  </button>
                </div>
                {expandedMember === m.user_id && (
                  <div className="px-4 pb-3 space-y-2 border-t pt-2">
                    {(memberEntries[m.user_id] || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No active entries</p>
                    ) : (memberEntries[m.user_id] || []).map(entry => (
                      <div key={entry.id} className="flex items-center justify-between bg-white rounded-lg p-2.5 border">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold">Entry {entry.entry_number}</span>
                            <span className="text-xs text-muted-foreground">{entry.display_name}</span>
                            {entry.rank > 0 && <Badge variant="secondary" className="text-[9px]">Rank #{entry.rank}</Badge>}
                            <span className="text-xs font-bold text-[#5b3cc4]">{entry.total_points} pts</span>
                          </div>
                          {entry.picks?.length > 0 && (
                            <div className="flex gap-1 mt-1">{entry.picks.map((p, i) => (
                              <span key={p.tier} className="text-[9px] bg-secondary rounded px-1.5 py-0.5">T{p.tier}: {p.team_name?.split(' ')[0]}</span>
                            ))}</div>
                          )}
                        </div>
                        {pool.status !== 'locked' && (m.user_id !== pool.organizer_id || user?.role === 'admin') && (
                          <button onClick={() => removeEntry(entry.id, `${m.user_name} Entry ${entry.entry_number}`)} className="text-xs text-destructive hover:underline ml-2">Remove</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {members.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No members yet</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h3 className="font-semibold text-sm mb-2">Pool Status</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={async () => { await poolAPI.update(pool.id, { status: 'locked' }); toast.success('Pool locked'); onRefresh?.(); }}>Lock Pool</Button>
            <Button variant="outline" size="sm" onClick={async () => { await poolAPI.update(pool.id, { status: 'open' }); toast.success('Pool opened'); onRefresh?.(); }}>Unlock Pool</Button>
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10 ml-auto" onClick={async () => {
              if (!window.confirm(`Delete "${pool.name}" and all entries, members, and messages? This cannot be undone.`)) return;
              try { await adminAPI.deletePool(pool.id); toast.success('Pool deleted'); navigate('/pools'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
            }}>Delete Pool</Button>
          </div>
        </CardContent>
      </Card>

      {pool.status !== 'locked' && <PoolSettingsForm pool={pool} onRefresh={onRefresh} />}
      {pool.status === 'locked' && <PoolSettingsForm pool={pool} onRefresh={onRefresh} disabled />}
    </div>
  );
}

function PoolSettingsForm({ pool, onRefresh, disabled }) {
  const [form, setForm] = useState({
    name: pool.name || '',
    description: pool.description || '',
    organizer_fee: pool.organizer_fee || '',
    payment_email: pool.payment_email || '',
    entry_deadline: pool.entry_deadline || '',
    goals_prediction_enabled: pool.goals_prediction_enabled !== false,
    prize_config: pool.prize_config || { first: 45, second: 25, third: 15, middle: 10, goals: 5, last_description: '' },
  });
  const [saving, setSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const prizeTotal = (form.prize_config.first || 0) + (form.prize_config.second || 0) + (form.prize_config.third || 0) + (form.prize_config.middle || 0) + (form.prize_config.goals || 0);
  const updatePrize = (key, val) => setForm(f => ({ ...f, prize_config: { ...f.prize_config, [key]: key === 'last_description' ? val : parseFloat(val) || 0 } }));

  const handleSave = async () => {
    if (Math.abs(prizeTotal - 100) > 0.01) { toast.error(`Prize percentages must total 100% (currently ${prizeTotal}%)`); return; }
    setSaving(true);
    try {
      await poolAPI.update(pool.id, form);
      toast.success('Pool settings saved');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Card className={disabled ? 'opacity-60 pointer-events-none' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Pool Settings</CardTitle>
        {disabled && <p className="text-xs text-muted-foreground">Pool is locked. Unlock to edit settings.</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium">Pool Name</label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
          <div>
            <label className="text-xs font-medium">Entry Fee Details</label>
            <Input value={form.organizer_fee} onChange={(e) => setForm({...form, organizer_fee: e.target.value.replace(/\$/g, '')})} placeholder="e.g., 25 per entry" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Please do not use $ here.</p>
          </div>
          <div><label className="text-xs font-medium">Payment Email</label><Input type="email" value={form.payment_email} onChange={(e) => setForm({...form, payment_email: e.target.value})} /></div>
          <div><label className="text-xs font-medium">Entry Deadline</label><Input type="datetime-local" value={form.entry_deadline} onChange={(e) => setForm({...form, entry_deadline: e.target.value})} /></div>
          <div>
            <label className="text-xs font-medium">Pool PIN</label>
            <div className="relative">
              <Input value={showPin ? (pool.pin || '—') : '••••'} readOnly className="bg-muted opacity-60 pr-10" />
              <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Set during pool creation. Cannot be changed.</p>
          </div>
        </div>
        <div><label className="text-xs font-medium">Description</label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="min-h-[60px]" /></div>

        <div>
          <label className="text-xs font-medium mb-2 block">Prize Structure (must total 100%)</label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <div><label className="text-[10px] text-muted-foreground">1st %</label><Input type="number" value={form.prize_config.first} onChange={(e) => updatePrize('first', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-[10px] text-muted-foreground">2nd %</label><Input type="number" value={form.prize_config.second} onChange={(e) => updatePrize('second', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-[10px] text-muted-foreground">3rd %</label><Input type="number" value={form.prize_config.third} onChange={(e) => updatePrize('third', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-[10px] text-muted-foreground">Middle %</label><Input type="number" value={form.prize_config.middle} onChange={(e) => updatePrize('middle', e.target.value)} className="h-8 text-sm" /></div>
            <div><label className="text-[10px] text-muted-foreground">Goals %</label><Input type="number" value={form.prize_config.goals} onChange={(e) => updatePrize('goals', e.target.value)} className="h-8 text-sm" /></div>
            <div className="flex items-end"><span className={`text-xs font-semibold ${Math.abs(prizeTotal - 100) < 0.01 ? 'text-[#5b3cc4]' : 'text-destructive'}`}>= {prizeTotal}%</span></div>
          </div>
          <div className="mt-2"><label className="text-[10px] text-muted-foreground">Last Place Prize</label><Input value={form.prize_config.last_description} onChange={(e) => updatePrize('last_description', e.target.value)} className="h-8 text-sm" placeholder="e.g., Cellar Dweller Prize" /></div>
        </div>

        <Button onClick={handleSave} disabled={saving || Math.abs(prizeTotal - 100) > 0.01} className="bg-[#5b3cc4] hover:bg-[#4e33a8]">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ========== RULES TAB ==========
function RulesTab({ pool }) {
  const prize = pool?.prize_config || {};
  const scoring = pool?.scoring_config || {};

  return (
    <div className="space-y-6 max-w-3xl">
      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#5b3cc4]" />How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm leading-relaxed">
          <p>Pick <strong>one team from each of the 5 tiers</strong>. Each tier has 4 teams assigned based on the previous season's final standings. Your 5 teams earn pool points throughout the season based on their match results and tier multiplier.</p>
          <p>You must also <strong>predict the total number of goals</strong> scored by all 20 Premier League teams during the entire season. This prediction is used as a tiebreaker and for the Goals Prediction prize.</p>
          <p>Points accumulate over all 38 matchweeks. The entrant with the most total pool points at the end of the season wins.</p>
        </CardContent>
      </Card>

      {/* Scoring */}
      <Card>
        <CardHeader>
          <CardTitle>Scoring System</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Lower-tier teams earn more points per result. Losses always earn 0 points.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-semibold">Tier</th>
                  <th className="px-4 py-2 text-center font-semibold">Win Points</th>
                  <th className="px-4 py-2 text-center font-semibold">Draw Points</th>
                  <th className="px-4 py-2 text-center font-semibold">Loss Points</th>
                </tr>
              </thead>
              <tbody>
                {[1,2,3,4,5].map(tier => {
                  const t = scoring[String(tier)] || {};
                  return (
                    <tr key={tier} className="border-b">
                      <td className="px-4 py-2.5 font-semibold">Tier {tier}</td>
                      <td className="px-4 py-2.5 text-center font-mono font-bold text-[#5b3cc4]">{t.win || 0}</td>
                      <td className="px-4 py-2.5 text-center font-mono">{t.draw || 0}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-muted-foreground">0</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Prize Structure */}
      <Card>
        <CardHeader>
          <CardTitle>Prize Structure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm leading-relaxed">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left font-semibold">Prize</th>
                  <th className="px-4 py-2 text-center font-semibold">% of Purse</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: '1st Place Overall', key: 'first' },
                  { label: '2nd Place Overall', key: 'second' },
                  { label: '3rd Place Overall', key: 'third' },
                  { label: 'Middle of the Pack', key: 'middle' },
                  { label: 'Goals Prediction', key: 'goals' },
                ].map(p => (
                  <tr key={p.key} className="border-b">
                    <td className="px-4 py-2.5 font-medium">{p.label}</td>
                    <td className="px-4 py-2.5 text-center font-mono font-bold text-[#5b3cc4]">{prize[p.key] || 0}%</td>
                  </tr>
                ))}
                {prize.last_description && (
                  <tr className="border-b">
                    <td className="px-4 py-2.5 font-medium">Last Place (Cellar Dweller)</td>
                    <td className="px-4 py-2.5 text-center text-sm">{prize.last_description}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Prize Rules & Tiebreakers */}
      <Card>
        <CardHeader>
          <CardTitle>Tiebreaker Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed">
          <div>
            <h4 className="font-semibold mb-1">1st Place Overall</h4>
            <p className="text-muted-foreground">Awarded to the entrant with the most accumulated points. In the event of a tie, the entry with the closest Goals Prediction (positive or negative variance) wins. If the Goals Prediction is also tied, the entrants share 1st and 2nd place prizes ({prize.first || 45}% + {prize.second || 25}% = {(prize.first || 45) + (prize.second || 25)}% of the total purse), split evenly. If three or more are tied, they share the top three prizes ({(prize.first || 45) + (prize.second || 25) + (prize.third || 15)}% of the purse), split evenly.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2nd Place Overall</h4>
            <p className="text-muted-foreground">Awarded to the entrant with the second most points. Tiebreaker: closest Goals Prediction. If still tied, 2nd and 3rd prizes are combined ({prize.second || 25}% + {prize.third || 15}% = {(prize.second || 25) + (prize.third || 15)}%) and split evenly.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">3rd Place Overall</h4>
            <p className="text-muted-foreground">Awarded to the entrant with the third most points. Tiebreaker: closest Goals Prediction. If still tied, the 3rd place prize ({prize.third || 15}%) is split evenly.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Middle of the Pack</h4>
            <p className="text-muted-foreground">Awarded to the entrant(s) at or closest to the middle position of the entire table. The middle position is calculated as half the total number of entrants, rounded to the nearest whole number (e.g., 251 entrants = position 126). If the middle number is skipped due to ties, the prize goes to the entrant(s) closest to the middle number toward the bottom of the pool. The tiebreaker rule does not apply. Prize is split evenly if multiple entrants qualify.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Goals Prediction</h4>
            <p className="text-muted-foreground">Awarded to the entrant(s) who predict closest to the total number of goals scored by all 20 Premier League teams during the season. The variance can be positive or negative. If predictions are equal, the prize is split evenly. The tiebreaker rule does not apply.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Cellar Dweller (Last Place)</h4>
            <p className="text-muted-foreground">Awarded to the entrant with the fewest accumulated points. Tiebreaker: closest Goals Prediction. If still tied, the entrants may agree to split the prize OR have their names drawn for a winner-take-all blind draw.{prize.last_description ? ` Prize: ${prize.last_description}.` : ''}</p>
          </div>
        </CardContent>
      </Card>

      {/* Entry Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Entry Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm leading-relaxed">
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">1.</span>Pick one team from each of the 5 tiers. Predict total number of goals scored by all 20 EPL teams during the regular season.</li>
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">2.</span>Maximum 2 entries per person per pool.</li>
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">3.</span>Entry fee must be paid prior to the start of the season. If not, entry will be removed from the pool with no ability to buy back in.</li>
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">4.</span>Picks can be edited until the entry deadline. After the deadline, all picks are permanently locked.</li>
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">5.</span>Goals prediction is mandatory and must be a whole number (integer).</li>
            <li className="flex gap-2"><span className="text-[#5b3cc4] font-bold">6.</span>No refunds on entry fees once payment is processed.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== MATCH CENTRE TAB ==========
function MatchCentreTab({ pool }) {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [poolTeamData, setPoolTeamData] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [topScorers, setTopScorers] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [playerTab, setPlayerTab] = useState('scorers');
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [loadingPlayerDetail, setLoadingPlayerDetail] = useState(false);
  const [playerSeason, setPlayerSeason] = useState(2026);

  const loadPlayerDetail = async (playerId) => {
    if (selectedPlayer === playerId) { setSelectedPlayer(null); setPlayerDetail(null); return; }
    setSelectedPlayer(playerId);
    setLoadingPlayerDetail(true);
    try {
      const res = await matchAPI.playerStats(playerId);
      const data = res.data.player || null;
      if (data && data.statistics) {
        // Find EPL (league 39) stats specifically
        const eplStats = data.statistics.find(s => s.league?.id === 39);
        if (eplStats) {
          setPlayerDetail({ player: data.player, statistics: [eplStats] });
        } else {
          // Fallback to first stats entry
          setPlayerDetail(data);
        }
      } else {
        setPlayerDetail(data);
      }
    } catch (err) { console.error(err); setPlayerDetail(null); }
    finally { setLoadingPlayerDetail(false); }
  };

  useEffect(() => {
    const loadPlayers = async () => {
      setLoadingPlayers(true);
      try {
        const [scorersRes, assistsRes] = await Promise.all([
          matchAPI.topScorers(playerSeason),
          matchAPI.topAssists(playerSeason),
        ]);
        setTopScorers(scorersRes.data.players || []);
        setTopAssists(assistsRes.data.players || []);
      } catch (err) { console.error(err); }
      finally { setLoadingPlayers(false); }
    };
    loadPlayers();
  }, [playerSeason]);

  const [teams, setTeams] = useState([]);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const res = await dataAPI.getAllTeams();
        const apiTeams = (res.data.teams || []).map(t => {
          const shortNames = {
            'Manchester City': 'Man City', 'Manchester United': 'Man Utd',
            'Crystal Palace': 'C. Palace', 'Nottingham Forest': 'Nott Forest',
            'Ipswich Town': 'Ipswich',
          };
          return {
            name: shortNames[t.name] || t.name,
            fullName: t.name,
            id: t.api_id,
            tier: t.tier,
          };
        });
        setTeams(apiTeams);
      } catch (err) { console.error(err); }
    };
    loadTeams();
  }, []);

  const multipliers = { 1: { w: 2, d: 1 }, 2: { w: 4, d: 2 }, 3: { w: 6, d: 3 }, 4: { w: 8, d: 4 }, 5: { w: 10, d: 5 } };

  const loadTeamStats = async (teamId) => {
    if (selectedTeam === teamId) { setSelectedTeam(null); setTeamStats(null); setPoolTeamData(null); return; }
    setSelectedTeam(teamId);
    setLoadingTeam(true);
    try {
      // Fetch both API stats and pool data in parallel
      const [apiRes, poolRes] = await Promise.all([
        matchAPI.teamStats(teamId),
        scoringAPI.leaderboard(pool.id, { page_size: 1 }),
      ]);

      setTeamStats(apiRes.data.statistics || null);

      // Get pool points for this team using fullName for exact matching
      const team = teams.find(t => t.id === teamId);
      const teamPts = poolRes.data.team_points || {};
      const fullName = team?.fullName || team?.name || '';
      const matchedKey = Object.keys(teamPts).find(n => n === fullName || n.toLowerCase() === fullName.toLowerCase());
      setPoolTeamData({ poolPoints: matchedKey ? teamPts[matchedKey] : 0, tier: team?.tier });
    } catch (err) { console.error(err); setTeamStats(null); }
    finally { setLoadingTeam(false); }
  };

  const StatBox = ({ label, value, sub }) => (
    <div className="text-center py-3">
      <div className="font-heading text-xl font-bold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      {sub && <div className="text-[9px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );

  const s = teamStats; // shorthand
  const fixtures = s?.fixtures;
  const goals = s?.goals;
  const biggest = s?.biggest;
  const clean = s?.clean_sheet;
  const penalty = s?.penalty;

  return (
    <div className="space-y-6">
      {/* 1. Team Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">Team Statistics</CardTitle>
          <p className="text-xs text-muted-foreground">Click any team for detailed season stats, pool points, form, goals, and more.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-10 gap-2 mb-4">
            {teams.map(team => (
              <button key={team.id} onClick={() => loadTeamStats(team.id)}
                className={`rounded-lg border p-2 text-center transition-all hover:bg-muted/50 ${selectedTeam === team.id ? 'border-[#5b3cc4] bg-[#f3f0ff]/30 ring-1 ring-[#5b3cc4]/30' : ''}`}>
                <img src={`https://media.api-sports.io/football/teams/${team.id}.png`} alt="" className="h-6 w-6 mx-auto mb-1" onError={(e) => { e.target.style.display='none'; }} />
                <span className="text-[10px] font-medium leading-tight block">{team.name}</span>
              </button>
            ))}
          </div>

          {loadingTeam && <div className="h-48 bg-muted animate-pulse rounded-xl" />}

          {selectedTeam && s && !loadingTeam && (
            <div className="overflow-x-auto rounded-xl border">
              <div style={{ minWidth: '600px' }}>
              {/* Header */}
              <div className="bg-gradient-to-r from-[#5b3cc4] to-[#6366f1] px-5 py-4 text-white flex items-center gap-4">
                <img src={s.team?.logo || `https://media.api-sports.io/football/teams/${selectedTeam}.png`} alt="" className="h-14 w-14" />
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold">{s.team?.name || 'Team'}</h3>
                  <p className="text-white/80 text-sm">
                    Tier {poolTeamData?.tier} — Win: {multipliers[poolTeamData?.tier]?.w}x | Draw: {multipliers[poolTeamData?.tier]?.d}x
                  </p>
                  {s.form && (
                    <div className="flex gap-1 mt-2">
                      {s.form.split('').slice(-8).map((r, i) => (
                        <div key={`form-${i}`} className={`h-5 w-5 rounded text-[10px] font-bold flex items-center justify-center ${r === 'W' ? 'bg-green-400 text-green-900' : r === 'D' ? 'bg-yellow-400 text-yellow-900' : 'bg-red-400 text-red-900'}`}>{r}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-heading text-3xl font-bold">{poolTeamData?.poolPoints || 0}</div>
                  <div className="text-white/70 text-xs">Pool Points</div>
                </div>
              </div>

              {/* Season Record */}
              {fixtures && (
                <div>
                  <div className="px-5 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Season Record</div>
                  <div className="grid grid-cols-8 divide-x border-b">
                      <StatBox label="Played" value={fixtures.played?.total ?? '-'} />
                      <StatBox label="Wins" value={fixtures.wins?.total ?? '-'} sub={`H:${fixtures.wins?.home ?? '-'} A:${fixtures.wins?.away ?? '-'}`} />
                      <StatBox label="Draws" value={fixtures.draws?.total ?? '-'} sub={`H:${fixtures.draws?.home ?? '-'} A:${fixtures.draws?.away ?? '-'}`} />
                      <StatBox label="Losses" value={fixtures.loses?.total ?? '-'} sub={`H:${fixtures.loses?.home ?? '-'} A:${fixtures.loses?.away ?? '-'}`} />
                      {goals && <>
                        <StatBox label="Goals For" value={goals.for?.total?.total ?? '-'} sub={`H:${goals.for?.total?.home ?? '-'} A:${goals.for?.total?.away ?? '-'}`} />
                        <StatBox label="Goals Agst" value={goals.against?.total?.total ?? '-'} sub={`H:${goals.against?.total?.home ?? '-'} A:${goals.against?.total?.away ?? '-'}`} />
                        <StatBox label="Avg Scored" value={goals.for?.average?.total ?? '-'} sub="per match" />
                        <StatBox label="Avg Conceded" value={goals.against?.average?.total ?? '-'} sub="per match" />
                      </>}
                    </div>
                </div>
              )}

              {/* Streaks & Records */}
              {biggest && (
                <div>
                  <div className="px-5 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Streaks & Records</div>
                  <div className="grid grid-cols-6 divide-x border-b">
                      <StatBox label="Win Streak" value={biggest.streak?.wins ?? '-'} sub="matches" />
                      <StatBox label="Draw Streak" value={biggest.streak?.draws ?? '-'} sub="matches" />
                      <StatBox label="Loss Streak" value={biggest.streak?.loses ?? '-'} sub="matches" />
                      <StatBox label="Biggest Win" value={biggest.wins?.home || biggest.wins?.away || '-'} />
                      <StatBox label="Biggest Loss" value={biggest.loses?.home || biggest.loses?.away || '-'} />
                      <StatBox label="Clean Sheets" value={(clean?.total ?? '-')} sub={`H:${clean?.home ?? '-'} A:${clean?.away ?? '-'}`} />
                  </div>
                </div>
              )}

              {/* Failed to Score & Lineups */}
              <div>
                <div className="px-5 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Additional Stats</div>
                <div className="grid grid-cols-4 divide-x border-b">
                  <StatBox label="Failed to Score" value={s?.failed_to_score?.total ?? '-'} sub={`H:${s?.failed_to_score?.home ?? '-'} A:${s?.failed_to_score?.away ?? '-'}`} />
                  {s?.lineups && s.lineups.length > 0 && (
                    <StatBox label="Top Formation" value={s.lineups[0]?.formation ?? '-'} sub={`${s.lineups[0]?.played ?? 0} matches`} />
                  )}
                  {s?.lineups && s.lineups.length > 1 && (
                    <StatBox label="2nd Formation" value={s.lineups[1]?.formation ?? '-'} sub={`${s.lineups[1]?.played ?? 0} matches`} />
                  )}
                  {s?.cards && (
                    <StatBox label="Cards" value={`${Object.values(s.cards.yellow || {}).reduce((sum, v) => sum + (v?.total || 0), 0)}Y ${Object.values(s.cards.red || {}).reduce((sum, v) => sum + (v?.total || 0), 0)}R`} sub="season total" />
                  )}
                  </div>
              </div>

              {/* Goals by Time Period */}
              {goals?.for?.minute && (
                <div>
                  <div className="px-5 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Goals Scored by Time</div>
                  <div className="px-5 py-4">
                    <div className="flex items-end gap-2 h-32">
                      {Object.entries(goals.for.minute).map(([period, data]) => {
                        const total = data?.total || 0;
                        const maxGoals = Math.max(...Object.values(goals.for.minute).map(d => d?.total || 0), 1);
                        const heightPct = (total / maxGoals) * 100;
                        return (
                          <div key={period} className="flex-1 flex flex-col items-center gap-1.5">
                            <span className="text-lg font-heading font-bold">{total}</span>
                            <div className="w-full rounded-t" style={{ height: `${Math.max(heightPct, 4)}%`, background: 'linear-gradient(to top, #5b3cc4, #818cf8)' }} />
                            <span className="text-sm font-medium text-muted-foreground leading-tight text-center">{period}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Penalty Stats */}
              {penalty && (penalty.scored?.total > 0 || penalty.missed?.total > 0) && (
                <div>
                  <div className="px-5 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Penalties</div>
                  <div className="grid grid-cols-3 divide-x border-b">
                    <StatBox label="Scored" value={penalty.scored?.total ?? 0} sub={penalty.scored?.percentage || ''} />
                    <StatBox label="Missed" value={penalty.missed?.total ?? 0} sub={penalty.missed?.percentage || ''} />
                    <StatBox label="Total" value={(penalty.scored?.total || 0) + (penalty.missed?.total || 0)} />
                  </div>
                </div>
              )}

              {/* Pool Points Breakdown */}
              <div className="px-5 py-3 bg-[#f3f0ff]/30 text-sm">
                <span className="text-muted-foreground">Pool points: </span>
                <span className="font-medium">{fixtures?.wins?.total || 0} wins × {multipliers[poolTeamData?.tier]?.w} + {fixtures?.draws?.total || 0} draws × {multipliers[poolTeamData?.tier]?.d} = </span>
                <span className="font-bold text-[#5b3cc4]">{poolTeamData?.poolPoints || 0} pts</span>
              </div>
              </div>
            </div>
          )}

          {selectedTeam && !s && !loadingTeam && (
            <div className="text-center py-8 text-sm text-muted-foreground">Stats not available for this team.</div>
          )}
        </CardContent>
      </Card>

      {/* 2. Top Scorers & Assists */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-[#5b3cc4]" />Top Players</CardTitle>
            <div className="inline-flex rounded-full border bg-muted p-0.5">
              <button className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${playerSeason === 2026 ? 'bg-[#5b3cc4] text-white shadow' : 'text-muted-foreground'}`} onClick={() => setPlayerSeason(2026)}>2026/27</button>
              <button className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${playerSeason === 2025 ? 'bg-[#5b3cc4] text-white shadow' : 'text-muted-foreground'}`} onClick={() => setPlayerSeason(2025)}>2025/26</button>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant={playerTab === 'scorers' ? 'default' : 'outline'} size="sm" onClick={() => setPlayerTab('scorers')} className={playerTab === 'scorers' ? 'bg-[#5b3cc4] hover:bg-[#4e33a8]' : ''}>Top Scorers</Button>
            <Button variant={playerTab === 'assists' ? 'default' : 'outline'} size="sm" onClick={() => setPlayerTab('assists')} className={playerTab === 'assists' ? 'bg-[#5b3cc4] hover:bg-[#4e33a8]' : ''}>Top Assists</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingPlayers ? (
            <div className="h-48 bg-muted animate-pulse rounded-xl" />
          ) : topScorers.length === 0 && topAssists.length === 0 ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <Users className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <h4 className="font-heading text-base font-semibold mb-1">Player stats available when the season starts</h4>
              <p className="text-sm text-muted-foreground mb-3">The 2026/27 EPL season kicks off August 21, 2026. Player statistics will populate as matches are played.</p>
              <Button variant="outline" size="sm" onClick={() => setPlayerSeason(2025)}>View 2025/26 Season Stats</Button>
            </div>
          ) : (
            <div className="rounded-xl border overflow-x-auto">
              <div style={{ minWidth: '650px' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground w-10">#</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Player</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Team</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-muted-foreground">Apps</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-[#5b3cc4] font-bold">{playerTab === 'scorers' ? 'Goals' : 'Assists'}</th>
                    {playerTab === 'scorers' && <th className="px-4 py-2.5 text-center text-xs font-semibold text-muted-foreground">Assists</th>}
                    {playerTab === 'assists' && <th className="px-4 py-2.5 text-center text-xs font-semibold text-muted-foreground">Goals</th>}
                  </tr>
                </thead>
                <tbody>
                  {(playerTab === 'scorers' ? topScorers : topAssists).slice(0, 20).map((item, idx) => {
                    const player = item.player || {};
                    const stats = item.statistics?.[0] || {};
                    const goals = stats.goals?.total || 0;
                    const assists = stats.goals?.assists || 0;
                    const apps = stats.games?.appearences || 0;
                    const teamName = stats.team?.name || '';
                    const teamLogo = stats.team?.logo || '';
                    const isExpanded = selectedPlayer === player.id;
                    return (
                      <React.Fragment key={player.id || idx}>
                        <tr className="border-b hover:bg-accent/30 transition-colors cursor-pointer" onClick={() => loadPlayerDetail(player.id)}>
                          <td className="px-4 py-2.5 font-mono text-sm font-bold text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2.5">
                              <img src={player.photo} alt="" className="h-8 w-8 rounded-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-sm">{player.name}</span>
                                  <ChevronDown className={`h-3.5 w-3.5 text-[#5b3cc4] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                                <div className="text-[10px] text-muted-foreground">{player.nationality} · Age {player.age}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <img src={teamLogo} alt="" className="h-5 w-5" onError={(e) => { e.target.style.display='none'; }} />
                              <span className="text-sm">{teamName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-center font-mono text-sm">{apps}</td>
                          <td className="px-4 py-2.5 text-center font-mono text-lg font-bold text-[#5b3cc4]">{playerTab === 'scorers' ? goals : assists}</td>
                          {playerTab === 'scorers' && <td className="px-4 py-2.5 text-center font-mono text-sm text-muted-foreground">{assists}</td>}
                          {playerTab === 'assists' && <td className="px-4 py-2.5 text-center font-mono text-sm text-muted-foreground">{goals}</td>}
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="p-0">
                              {loadingPlayerDetail ? (
                                <div className="h-32 bg-muted animate-pulse m-3 rounded-xl" />
                              ) : playerDetail ? (() => {
                                const p = playerDetail.player || {};
                                const st = playerDetail.statistics?.[0] || {};
                                const g = st.games || {};
                                const gl = st.goals || {};
                                const sh = st.shots || {};
                                const ps = st.passes || {};
                                const tk = st.tackles || {};
                                const dr = st.dribbles || {};
                                const du = st.duels || {};
                                const sb = st.substitutes || {};
                                const fl = st.fouls || {};
                                const cd = st.cards || {};
                                const pn = st.penalty || {};
                                return (
                                  <div className="bg-muted/20 p-2 sm:p-4">
                                    <div className="rounded-xl border bg-card overflow-x-auto">
                                      <div style={{ minWidth: '560px' }}>
                                      {/* Player header */}
                                      <div className="bg-gradient-to-r from-[#5b3cc4] to-[#6366f1] px-5 py-4 text-white flex items-center gap-4">
                                        <img src={p.photo || `https://media.api-sports.io/football/players/${p.id}.png`} alt="" className="h-16 w-16 rounded-full border-2 border-white/30 object-cover" />
                                        <div className="flex-1">
                                          <h3 className="font-heading text-xl font-bold">{p.name || p.firstname + ' ' + p.lastname}</h3>
                                          <p className="text-white/80 text-sm">{st.team?.name} · {g.position || 'Player'}</p>
                                          <p className="text-white/60 text-xs mt-0.5">{p.nationality} · Age {p.age} · {p.height} · {p.weight}</p>
                                        </div>
                                        {g.rating && (
                                          <div className="text-right">
                                            <div className="font-heading text-3xl font-bold">{parseFloat(g.rating).toFixed(1)}</div>
                                            <div className="text-white/70 text-xs">Rating</div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Key stats */}
                                        <div className="grid grid-cols-5 divide-x border-b">
                                        {[
                                          { label: 'Apps', val: g.appearences || 0, sub: `${g.lineups || 0} starts, ${sb.in || 0} sub` },
                                          { label: 'Minutes', val: g.minutes || 0 },
                                          { label: 'Goals', val: gl.total || 0 },
                                          { label: 'Assists', val: gl.assists || 0 },
                                          { label: 'Rating', val: g.rating ? parseFloat(g.rating).toFixed(1) : '-' },
                                        ].map(s => (
                                          <div key={s.label} className="py-3 text-center">
                                            <div className="font-heading text-lg font-bold">{s.val}</div>
                                            <div className="text-[10px] text-muted-foreground">{s.label}</div>
                                            {s.sub && <div className="text-[9px] text-muted-foreground">{s.sub}</div>}
                                          </div>
                                        ))}
                                      </div>

                                      {/* Detailed stats */}
                                        <div className="grid grid-cols-8 divide-x border-b">
                                        {[
                                          { label: 'Shots', val: sh.total || 0, sub: `${sh.on || 0} on target` },
                                          { label: 'Passes', val: ps.total || 0, sub: ps.accuracy ? `${ps.accuracy}% acc` : `${ps.key || 0} key` },
                                          { label: 'Tackles', val: tk.total || 0, sub: `${tk.blocks || 0} blk, ${tk.interceptions || 0} int` },
                                          { label: 'Dribbles', val: dr.attempts || 0, sub: `${dr.success || 0} success` },
                                          { label: 'Duels', val: du.total || 0, sub: `${du.won || 0} won` },
                                          { label: 'Fouls', val: fl.committed || 0, sub: `${fl.drawn || 0} drawn` },
                                          { label: 'Yellow', val: cd.yellow || 0 },
                                          { label: 'Red', val: cd.red || 0 },
                                        ].map(s => (
                                          <div key={s.label} className="py-3 text-center">
                                            <div className="font-heading text-base font-bold">{s.val}</div>
                                            <div className="text-[10px] text-muted-foreground">{s.label}</div>
                                            {s.sub && <div className="text-[9px] text-muted-foreground">{s.sub}</div>}
                                          </div>
                                        ))}
                                      </div>

                                      {/* Penalties */}
                                      {(pn.scored > 0 || pn.missed > 0) && (
                                        <div className="grid grid-cols-3 divide-x">
                                          {[
                                            { label: 'Pens Scored', val: pn.scored || 0 },
                                            { label: 'Pens Missed', val: pn.missed || 0 },
                                            { label: 'Pens Won', val: pn.won || 0 },
                                          ].map(s => (
                                            <div key={s.label} className="py-2 text-center">
                                              <div className="font-heading text-base font-bold">{s.val}</div>
                                              <div className="text-[10px] text-muted-foreground">{s.label}</div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })() : (
                                <div className="text-center py-6 text-sm text-muted-foreground">Player stats not available.</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. League Table Widget */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base"><Trophy className="h-4 w-4 text-[#5b3cc4]" />EPL League Table</CardTitle>
            <Badge className="bg-green-100 text-green-700 text-xs">LIVE</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border overflow-hidden bg-white">
            <iframe src="/widget-embed.html?type=standings" className="w-full border-0" style={{ height: '680px' }} title="EPL Standings" />
          </div>
        </CardContent>
      </Card>

      {/* 3. Fixtures Widget */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base"><Tv className="h-4 w-4 text-[#5b3cc4]" />This Week's EPL Matches</CardTitle>
            <Badge className="bg-green-100 text-green-700 text-xs">LIVE</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border overflow-hidden bg-white">
            <iframe src="/widget-embed.html?type=games" className="w-full border-0" style={{ height: '420px' }} title="EPL Fixtures" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== POOL DETAIL MAIN ==========

export default function PoolDetail() {
  const { poolId } = useParams();
  const { user } = useAuth();
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({ unread_chat: 0, unread_announcements: 0 });
  const navigate = useNavigate();

  const loadPool = useCallback(async () => {
    try {
      const res = await poolAPI.get(poolId);
      setPool(res.data.pool);
      document.title = `${res.data.pool.name} — Pools FC`;
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [poolId]);

  const loadUnread = useCallback(async () => {
    try {
      const res = await chatAPI.unread(poolId);
      setUnread(res.data);
    } catch (e) { console.error(e); }
  }, [poolId]);

  useEffect(() => { loadPool(); }, [loadPool]);
  useEffect(() => { loadUnread(); const iv = setInterval(loadUnread, 15000); return () => clearInterval(iv); }, [loadUnread]);

  if (loading) return <div className="animate-pulse h-96 bg-muted rounded-2xl" />;
  if (!pool) return <div className="text-center py-12"><p className="text-muted-foreground">Pool not found</p></div>;

  // Not a member — redirect to join
  if (!pool.is_member) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="font-heading text-xl font-bold mb-2">{pool.name}</h2>
        <p className="text-muted-foreground mb-6">Join this pool to see full details, standings, and chat.</p>
        <Button asChild size="lg"><Link to={`/pools/${poolId}/join`}>Join Pool</Link></Button>
      </div>
    );
  }

  const isOrganizer = pool.organizer_id === user?.id;
  const isAdmin = user?.role === 'admin';
  const canManage = isOrganizer || isAdmin;
  const inviteUrl = `${window.location.origin}/invite/${pool.invite_code}`;
  const isPaidPlan = pool.plan && pool.plan !== 'free';

  return (
    <div>
      <div className="relative overflow-hidden rounded-2xl border bg-card px-4 sm:px-6 py-5 mb-6 noise-soft">
        <div className="absolute inset-0 opacity-[0.04] blur-sm" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1550591927-391fccf9934b?w=1200&q=15)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors">
            <ChevronUp className="h-3 w-3 -rotate-90" />Back to Pools
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">{pool.name}</h1>
            {pool.description && <p className="text-muted-foreground text-sm mt-0.5">{pool.description}</p>}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{pool.entry_count} entries</span>
              <span className="flex items-center gap-1">Entry fee: {pool.organizer_fee || "Details in pool"}</span>
              <Badge className={pool.status === 'open' ? 'bg-green-100 text-green-800 text-[10px]' : 'bg-yellow-100 text-yellow-800 text-[10px]'}>{pool.status}</Badge>
              <Badge className="bg-[#f3f0ff] text-[#5b3cc4] text-[10px]">{pool.plan_label || 'Free'} Plan · {pool.plan_capacity || 8} max</Badge>
              <span>by {pool.organizer_name}</span>
            </div>
            {pool.payment_email && (
              <p className="text-xs mt-1">Entry fee contributions should be sent to: <span className="font-medium text-[#5b3cc4]">{pool.payment_email}</span></p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {pool.status === 'open' && (
              <Button size="sm" asChild><Link to={`/pools/${poolId}/join`}><Trophy className="h-3.5 w-3.5 mr-1" />Add Entry</Link></Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success('Invite link copied!'); }}>
              <Copy className="h-3.5 w-3.5 mr-1" />Invite
            </Button>
          </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="leaderboard" onValueChange={(v) => { if (v === 'announcements' || v === 'chat') setTimeout(loadUnread, 1000); }}>
        <TabsList className="mb-4 flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="text-xs sm:text-sm"><Trophy className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Standings</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements" className="relative text-xs sm:text-sm">
            <Megaphone className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Announce
            {unread.unread_announcements > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">{unread.unread_announcements}</span>}
          </TabsTrigger>
          <TabsTrigger value="matchcentre" data-testid="tab-matchcentre" className="text-xs sm:text-sm"><Tv className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Match{!isPaidPlan && ' 🔒'}</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat" className="relative text-xs sm:text-sm">
            <MessageSquare className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Chat{!isPaidPlan && ' 🔒'}
            {isPaidPlan && unread.unread_chat > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">{unread.unread_chat}</span>}
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules" className="text-xs sm:text-sm"><BookOpen className="h-3.5 w-3.5 mr-1 hidden sm:inline" />Rules</TabsTrigger>
          {canManage && <TabsTrigger value="manage" data-testid="tab-manage" className="text-xs sm:text-sm">Manage</TabsTrigger>}
        </TabsList>
        <TabsContent value="leaderboard"><LeaderboardTab poolId={poolId} pool={pool} /></TabsContent>
        <TabsContent value="announcements"><AnnouncementsTab poolId={poolId} pool={pool} /></TabsContent>
        <TabsContent value="matchcentre">
          {isPaidPlan ? <MatchCentreTab pool={pool} /> : (
            <Card className="border-[#5b3cc4]/20">
              <CardContent className="py-12 text-center">
                <Tv className="h-12 w-12 mx-auto text-[#5b3cc4]/40 mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Match Centre</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">Get live EPL fixtures, league table, team statistics with detailed breakdowns, and top player stats. Available on all paid plans.</p>
                <div className="flex flex-wrap justify-center gap-3 mb-6 text-xs text-muted-foreground">
                  {['Live Fixtures','League Table','Team Stats & Form','Top Scorers & Assists','Player Profiles'].map(f => (
                    <span key={f} className="bg-secondary px-3 py-1.5 rounded-full">{f}</span>
                  ))}
                </div>
                {isOrganizer ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Upgrade your pool plan to unlock:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {[
                        {plan:'basic',label:'Basic',price:'47',cap:'8 entrants'},
                        {plan:'starter',label:'Starter',price:'97',cap:'25 entrants'},
                        {plan:'standard',label:'Standard',price:'197',cap:'99 entrants'},
                        {plan:'pro',label:'Pro',price:'397',cap:'400 entrants'},
                      ].map(p => (
                        <Button key={p.plan} variant="outline" className="border-[#5b3cc4]/30 hover:bg-[#f3f0ff]"
                          onClick={async () => { try { await poolAPI.upgrade(poolId, p.plan); toast.success(`Upgraded to ${p.label}!`); loadPool(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } }}>
                          <div className="text-left"><div className="font-semibold">{p.label} — {p.price}/season</div><div className="text-[10px] text-muted-foreground">{p.cap}</div></div>
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Ask the pool organizer to upgrade to a paid plan.</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="chat">
          {isPaidPlan ? <ChatTab poolId={poolId} pool={pool} /> : (
            <Card className="border-[#5b3cc4]/20">
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-[#5b3cc4]/40 mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Pool Chat</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto mb-4">Discuss picks, celebrate wins, and talk football with your pool members. Available on all paid plans.</p>
                {isOrganizer ? (
                  <p className="text-sm text-[#5b3cc4]">Upgrade your pool plan in the Match Centre tab to unlock chat.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Ask the pool organizer to upgrade to a paid plan.</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="rules"><RulesTab pool={pool} /></TabsContent>
        {canManage && <TabsContent value="manage"><ManageTab pool={pool} onRefresh={loadPool} /></TabsContent>}
      </Tabs>
    </div>
  );
}
